
import { useState, useEffect } from 'react';
import { SubscriptionPlan } from '@/types/subscriptionPlans';
import { ResellerPlanDetails } from '@/hooks/license/resellerPlans/types';

// Create a union type that can be either SubscriptionPlan or ResellerPlanDetails
type PlanType = SubscriptionPlan | ResellerPlanDetails;

export const usePlanPricing = (currentPlan: PlanType | null, targetPlan: PlanType | null) => {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'quarterly' | 'yearly'>('monthly');
  const [newAmount, setNewAmount] = useState(0);
  const [prorationAmount, setProrationAmount] = useState(0);
  const [remainingDays, setRemainingDays] = useState(0);
  const [finalAmount, setFinalAmount] = useState(0);

  // Calculate prices when plans or billing cycle change
  useEffect(() => {
    if (!currentPlan || !targetPlan) {
      setNewAmount(0);
      setProrationAmount(0);
      setRemainingDays(0);
      setFinalAmount(0);
      return;
    }

    // Mock remaining days calculation
    const mockRemainingDays = 15; // Simulated value - in real app would be calculated from subscription data
    setRemainingDays(mockRemainingDays);

    // Get monthly price of current plan
    let currentMonthlyPrice = 0;
    if ('monthlyPrice' in currentPlan) {
      currentMonthlyPrice = currentPlan.monthlyPrice;
    } else {
      // Default price for reseller plan
      currentMonthlyPrice = 99;
    }

    // Get price of target plan based on billing cycle
    let targetPlanPrice = 0;
    if ('monthlyPrice' in targetPlan) {
      // This is a SubscriptionPlan type
      switch (billingCycle) {
        case 'monthly':
          targetPlanPrice = targetPlan.monthlyPrice;
          break;
        case 'quarterly':
          targetPlanPrice = targetPlan.quarterlyPrice;
          break;
        case 'yearly':
          targetPlanPrice = targetPlan.yearlyPrice || (targetPlan.monthlyPrice * 12 * 0.8); // 20% discount if not defined
          break;
        default:
          targetPlanPrice = targetPlan.monthlyPrice;
      }
    } else {
      // For ResellerPlanDetails, use standard pricing
      const standardMonthlyRate = 99;
      switch (billingCycle) {
        case 'monthly':
          targetPlanPrice = standardMonthlyRate;
          break;
        case 'quarterly':
          targetPlanPrice = standardMonthlyRate * 3 * 0.9; // 10% discount
          break;
        case 'yearly':
          targetPlanPrice = standardMonthlyRate * 12 * 0.8; // 20% discount
          break;
        default:
          targetPlanPrice = standardMonthlyRate;
      }
    }

    setNewAmount(targetPlanPrice);

    // Calculate proration for remaining days of current subscription
    const currentPlanDailyRate = currentMonthlyPrice / 30;
    const calculatedProration = parseFloat((currentPlanDailyRate * mockRemainingDays).toFixed(2));
    setProrationAmount(calculatedProration);

    // Calculate final amount
    const calculatedFinalAmount = Math.max(0, targetPlanPrice - calculatedProration);
    setFinalAmount(calculatedFinalAmount);
  }, [currentPlan, targetPlan, billingCycle]);

  return {
    billingCycle,
    setBillingCycle,
    newAmount,
    prorationAmount,
    remainingDays,
    finalAmount
  };
};
